import React, { useState } from 'react';
import { DiscountInput } from '../DiscountInput/DiscountInput';
import { formatMoney } from '../../../utils/currency';
import { getSubunitMultiplier, majorToAmount } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';
import type { CartItemProps } from './types';

// ─── CartItem ─────────────────────────────────────────────────────────────────

// Convert a user-entered discount (major-unit number from the inline input) into
// the line-item minor-unit amount the cart store expects. For `flat` mode this
// must use the tenant currency's subunit factor (×1000 for OMR/BHD/KWD) — a
// hardcoded ×100 silently 10×-truncates 3-decimal-currency discounts.
export function computeLineDiscountAmount(
  discountType: 'flat' | 'percent',
  discountValue: number,
  unitPrice: number,
  quantity: number,
  currency: CurrencyCode,
): number {
  return discountType === 'percent'
    ? Math.floor((unitPrice * quantity * discountValue) / 100)
    : majorToAmount(discountValue, currency);
}

export function CartItem({
  id,
  name,
  sku,
  quantity,
  unitPrice,
  discountAmount,
  billDiscountShare = 0,
  taxAmount,
  lineTotal,
  unit,
  decimalQuantity = false,
  warning,
  onQuantityChange,
  onDiscountChange,
  onRemove,
  currency = 'INR',
  className,
}: CartItemProps) {
  const fmt = (paise: number): string => formatMoney(paise, currency);
  const [showDiscount, setShowDiscount] = useState(false);
  // Typed quantity for a measured line; committed on blur / Enter.
  const [qtyDraft, setQtyDraft] = useState<string | null>(null);

  function commitQtyDraft() {
    if (qtyDraft === null) return;
    const qty = Math.round(Number(qtyDraft) * 1000) / 1000;
    setQtyDraft(null);
    if (Number.isFinite(qty) && qty > 0 && qty !== quantity) onQuantityChange(id, qty);
  }
  const [discountType, setDiscountType] = useState<'flat' | 'percent'>('flat');
  const [discountValue, setDiscountValue] = useState(0);

  function applyDiscount() {
    const amount = computeLineDiscountAmount(
      discountType,
      discountValue,
      unitPrice,
      quantity,
      currency,
    );
    onDiscountChange(id, amount);
    setShowDiscount(false);
  }

  return (
    <div
      className={[
        'group flex flex-col gap-1.5 px-3 py-2 rounded-lg border border-transparent',
        'hover:bg-neutral-50 hover:border-neutral-100 dark:hover:bg-neutral-800 dark:hover:border-neutral-700',
        'transition-colors',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* Top row: name + price + remove */}
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-neutral-800 dark:text-neutral-100 truncate">{name}</p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">{sku}{unit ? ` · ${unit}` : ''}</p>
        </div>
        <div className="text-sm font-semibold text-neutral-800 dark:text-neutral-100 shrink-0 text-right">
          {fmt(lineTotal)}
        </div>
        <button
          type="button"
          onClick={() => onRemove(id)}
          aria-label={`Remove ${name} from cart`}
          className="shrink-0 p-1 rounded text-neutral-300 dark:text-neutral-600 hover:text-danger-500 dark:hover:text-danger-400 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Bottom row: qty controls + unit price + discount toggle */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Quantity controls */}
        <div className="flex items-center gap-1 rounded border border-neutral-200 dark:border-neutral-600">
          <button
            type="button"
            aria-label="Decrease quantity"
            onClick={() => quantity > 1 && onQuantityChange(id, Math.round((quantity - 1) * 1000) / 1000)}
            disabled={quantity <= 1}
            className="px-2 py-1 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 disabled:opacity-30 rounded-l transition-colors text-sm font-bold"
          >
            −
          </button>
          {decimalQuantity ? (
            <input
              type="number"
              inputMode="decimal"
              step="0.001"
              min="0.001"
              aria-label={`Quantity of ${name}${unit ? ` in ${unit}` : ''}`}
              value={qtyDraft ?? String(quantity)}
              onChange={(e) => setQtyDraft(e.target.value)}
              onBlur={commitQtyDraft}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitQtyDraft();
                if (e.key === 'Escape') setQtyDraft(null);
              }}
              className="w-20 px-1 py-0.5 text-sm font-semibold text-center tabular-nums text-neutral-800 dark:text-neutral-100 bg-transparent border-x border-neutral-200 dark:border-neutral-600 focus:outline-none focus:ring-2 focus:ring-brand-500 transition-all duration-200"
            />
          ) : (
            <span className="px-3 text-sm font-semibold text-neutral-800 dark:text-neutral-100 select-none min-w-[2rem] text-center">
              {quantity}
            </span>
          )}
          <button
            type="button"
            aria-label="Increase quantity"
            onClick={() => onQuantityChange(id, Math.round((quantity + 1) * 1000) / 1000)}
            className="px-2 py-1 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-r transition-colors text-sm font-bold"
          >
            +
          </button>
        </div>

        <span className="text-xs text-neutral-500 dark:text-neutral-400">{fmt(unitPrice)} each</span>

        {discountAmount > 0 && (
          <span className="text-xs text-success-600 dark:text-success-400">-{fmt(discountAmount)} off</span>
        )}

        {billDiscountShare > 0 && (
          <span
            className="text-xs font-medium text-warning-700 dark:text-warning-400 bg-warning-50 dark:bg-warning-900/30 rounded px-1.5 py-0.5"
            title="Pro-rated share of bill-level discount"
          >
            -{fmt(billDiscountShare)} bill
          </span>
        )}

        {taxAmount > 0 && (
          <span className="text-xs text-neutral-400 dark:text-neutral-500">+{fmt(taxAmount)} tax</span>
        )}

        {/* Discount toggle */}
        <button
          type="button"
          onClick={() => setShowDiscount((v) => !v)}
          className="ml-auto text-xs text-brand-600 dark:text-brand-400 hover:underline transition-colors"
        >
          {showDiscount ? 'Cancel' : 'Discount'}
        </button>
      </div>

      {warning && (
        <p
          role="status"
          className="text-xs font-medium text-warning-700 dark:text-warning-400 bg-warning-50 dark:bg-warning-900/30 rounded px-1.5 py-0.5"
        >
          {warning}
        </p>
      )}

      {/* Inline discount input */}
      {showDiscount && (
        <div className="mt-1">
          <DiscountInput
            value={discountValue}
            discountType={discountType}
            onValueChange={setDiscountValue}
            onTypeChange={setDiscountType}
            onApply={applyDiscount}
            maxValue={(unitPrice * quantity) / getSubunitMultiplier(currency)}
            currency={currency}
          />
        </div>
      )}
    </div>
  );
}
